import { useMemo, useRef } from "react";
import { useTexture } from "@react-three/drei";
import * as THREE from "three";
import { GLOBE_RADIUS, vector3ToLatLon } from "../lib/seismic";
import { useSim } from "../lib/store";
import { FaultLines } from "./FaultLines";
import { EpicenterPin } from "./EpicenterPin";
import { EnergyParticles } from "./EnergyParticles";
import { VolcanoNodes } from "./VolcanoNodes";
import { VolcanoEruption } from "./VolcanoEruption";
import { AftershockSwarm } from "./AftershockSwarm";
import { FractureLines } from "./FractureLines";
import { CoastlineFlood } from "./CoastlineFlood";
import { TsunamiZones } from "./TsunamiZones";

export function Globe() {
  const groupRef = useRef();
  const simulating = useSim((s) => s.simulating);
  const handleGlobeClick = useSim((s) => s.handleGlobeClick);
  const events = useSim((s) => s.events);
  const draft = useSim((s) => s.draft);
  const clickStage = useSim((s) => s.clickStage);
  const setPinMenuId = useSim((s) => s.setPinMenuId);

  const strongestOffshore = useMemo(() => {
    let best = null;
    for (const e of events) if (e.offshore && (!best || e.magnitude > best.magnitude)) best = e;
    return best;
  }, [events]);

  const [colorMap, bumpMap, waterMap] = useTexture([
    "/data/earth-blue-marble.jpg",
    "/data/earth-topology.png",
    "/data/earth-water.png",
  ]);

  const globeMaterial = useMemo(() => {
    colorMap.colorSpace = THREE.SRGBColorSpace;
    colorMap.anisotropy = 8;
    [colorMap, bumpMap, waterMap].forEach((t) => {
      if (t) {
        // ClampToEdge stops UV pinching / seam artefacts at the poles.
        t.wrapS = THREE.ClampToEdgeWrapping;
        t.wrapT = THREE.ClampToEdgeWrapping;
        t.needsUpdate = true;
      }
    });
    const mat = new THREE.MeshStandardMaterial({
      map: colorMap,
      bumpMap: bumpMap,
      bumpScale: 0.05,
      metalnessMap: waterMap,
      metalness: 0.72,
      roughness: 0.48,
      emissive: new THREE.Color("#0a1424"),
      emissiveIntensity: 0.3,
    });
    mat.onBeforeCompile = (shader) => {
      shader.fragmentShader = shader.fragmentShader.replace(
        "#include <map_fragment>",
        `#include <map_fragment>
         float _lum = dot(diffuseColor.rgb, vec3(0.299, 0.587, 0.114));
         diffuseColor.rgb = clamp(mix(vec3(_lum), diffuseColor.rgb, 1.4), 0.0, 1.0);`
      );
    };
    return mat;
  }, [colorMap, bumpMap, waterMap]);

  // Click-vs-drag: only drop a pin on a stationary press+release (≤5px / small 3D move).
  // Dragging to rotate (mouse, touch-swipe, or VR pinch-drag) must NOT drop a pin.
  const downRef = useRef(null);

  const handlePointerDown = (e) => {
    if (simulating) return;
    const ne = e.nativeEvent || {};
    downRef.current = {
      x: ne.clientX ?? null,
      y: ne.clientY ?? null,
      point: e.point.clone(),
      t: performance.now(),
    };
  };

  const handlePointerUp = (e) => {
    const d = downRef.current;
    downRef.current = null;
    if (simulating || !d) return;
    const ne = e.nativeEvent || {};
    let isDrag;
    if (d.x != null && ne.clientX != null) {
      isDrag = Math.hypot(ne.clientX - d.x, ne.clientY - d.y) > 5; // 5px desktop/tablet/mobile
    } else {
      isDrag = d.point.distanceTo(e.point) > 0.06; // VR / no-pixel fallback (world units)
    }
    if (isDrag) return; // rotation gesture — ignore
    if (useSim.getState().pinMenuId) {
      setPinMenuId(null);
      return;
    }
    const local = groupRef.current.worldToLocal(e.point.clone());
    const { lat, lon } = vector3ToLatLon(local);
    handleGlobeClick(lat, lon);
  };

  return (
    <group ref={groupRef}>
      <mesh onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} castShadow receiveShadow material={globeMaterial}>
        <sphereGeometry args={[GLOBE_RADIUS, 128, 128]} />
      </mesh>

      {/* Polar caps — matte discs that hide any UV pinching at the poles (Antarctica gap) */}
      <mesh position={[0, -GLOBE_RADIUS + 0.002, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.34, 48]} />
        <meshStandardMaterial color="#eef2f6" roughness={0.9} metalness={0} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, GLOBE_RADIUS - 0.002, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.26, 48]} />
        <meshStandardMaterial color="#eef2f6" roughness={0.9} metalness={0} side={THREE.DoubleSide} />
      </mesh>

      <FaultLines radius={GLOBE_RADIUS * 1.004} />

      <mesh scale={1.045}>
        <sphereGeometry args={[GLOBE_RADIUS, 64, 64]} />
        <meshBasicMaterial color="#2a6bd6" transparent opacity={0.08} side={THREE.BackSide} blending={THREE.AdditiveBlending} depthWrite={false} />
      </mesh>

      {/* draft (gold) pin during placement */}
      {draft && clickStage === 1 && <EpicenterPin draft={draft} />}

      {/* committed events — each renders its own independent effect stack */}
      {events.map((e) => (
        <group key={e.id}>
          <EpicenterPin event={e} />
          <FractureLines event={e} />
          <EnergyParticles event={e} />
          <AftershockSwarm event={e} />
          <VolcanoEruption event={e} />
        </group>
      ))}

      <CoastlineFlood event={strongestOffshore} />
      <TsunamiZones count={200} />
      <VolcanoNodes />
    </group>
  );
}
